import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { Layout } from '@layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import { getEmployeePayrollByIds } from '@/services/HrService/PayrollService';
import { getCompanyInfo } from '@/services/CompanyInfoService';

interface SalaryComponent {
  id: string;
  label: string;
  amount: number | '';
  percentage: number | '';
  operation: 'add' | 'subtract';
  isPercentage: boolean;
}

interface EmployeePayroll {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  bankName?: string;
  accountNumber?: string;
  panNumber?: string;
  uanNumber?: string;
  grossSalary: number;
  salaryComponents: SalaryComponent[];
  netSalary: number;
}

// Shared cell styles
const labelCell = {
  border: '1px solid #bbb',
  py: 0.8,
  px: 1.5,
  bgcolor: '#f5f5f5',
  color: '#333',
  fontSize: '0.82rem',
  fontWeight: 500,
  width: '20%',
};

const valueCell = {
  border: '1px solid #bbb',
  py: 0.8,
  px: 1.5,
  color: '#111',
  fontSize: '0.82rem',
  width: '30%',
  borderBottom: '1px solid #888',
};

// Same file the PDF header uses (nucleus-api/public/assets/logos/company-logo.png),
// so the preview and the downloaded slip show the identical mark.
const COMPANY_LOGO_SRC = '/assets/logos/company-logo.png';

const ViewEmployeeSalarySlip: React.FC = () => {
  const { payrollId, employeePayrollId } = useParams<{ payrollId: string; employeePayrollId: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useAppTheme();

  const [loading, setLoading] = useState(true);
  const [employeePayroll, setEmployeePayroll] = useState<EmployeePayroll | null>(null);
  const [payrollInfo, setPayrollInfo] = useState<any>(null);
  const [companyInfo, setCompanyInfo] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!employeePayrollId || !payrollId) return;
      try {
        const companyResponse = await getCompanyInfo();
        if (companyResponse.success && companyResponse.data) {
          setCompanyInfo(companyResponse.data);
        }

        const empPayrollResponse = await getEmployeePayrollByIds(parseInt(payrollId), parseInt(employeePayrollId));
        if (empPayrollResponse.success && empPayrollResponse.data) {
          const ep = empPayrollResponse.data;
          setPayrollInfo({
            payroll_name: ep.payroll_name,
            start_date: ep.start_date,
            end_date: ep.end_date,
            pay_date: ep.pay_date,
          });

          let netSalary = parseFloat(ep.gross_salary);
          const salaryComponents = (ep.salary_components || []).map((comp: any, index: number) => {
            const componentValue =
              comp.component_type === 'percentage'
                ? (parseFloat(ep.gross_salary) * parseFloat(comp.value)) / 100
                : parseFloat(comp.value);

            if (comp.operation === 'add') netSalary += componentValue;
            else netSalary -= componentValue;

            return {
              id: `comp-${index}-${Date.now()}`,
              label: comp.component_name,
              amount: comp.component_type === 'fixed' ? parseFloat(comp.value) : 0,
              percentage: comp.component_type === 'percentage' ? parseFloat(comp.value) : 0,
              operation: comp.operation,
              isPercentage: comp.component_type === 'percentage',
            };
          });

          setEmployeePayroll({
            id: ep.id.toString(),
            employeeId: ep.employee_id,
            employeeName: ep.employee_name,
            employeeEmail: ep.employee_email || '',
            bankName: ep.bank_name,
            accountNumber: ep.account_number,
            panNumber: ep.pan_number,
            uanNumber: ep.uan_number,
            grossSalary: parseFloat(ep.gross_salary),
            salaryComponents,
            netSalary,
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [employeePayrollId, payrollId]);

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  if (!employeePayroll || !payrollInfo) {
    return (
      <Layout>
        <Container maxWidth="xl">
          <Box sx={{ p: 3 }}>
            <Typography>Employee payroll not found</Typography>
            <Button onClick={() => navigate(`/payroll/${payrollId}`)}>Back to Payroll</Button>
          </Box>
        </Container>
      </Layout>
    );
  }

  const month = new Date(payrollInfo.start_date).toLocaleString('default', { month: 'long' });
  const year = new Date(payrollInfo.start_date).getFullYear();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNum = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${monthNum}/${date.getFullYear()}`;
  };

  const getComponentAmount = (comp: SalaryComponent) =>
    comp.isPercentage
      ? (employeePayroll.grossSalary * (Number(comp.percentage) || 0)) / 100
      : Number(comp.amount) || 0;

  const earnings = employeePayroll.salaryComponents.filter((c) => c.operation === 'add');
  const deductions = employeePayroll.salaryComponents.filter((c) => c.operation === 'subtract');

  const totalEarnings =
    employeePayroll.grossSalary + earnings.reduce((sum, c) => sum + getComponentAmount(c), 0);
  const totalDeductions = deductions.reduce((sum, c) => sum + getComponentAmount(c), 0);

  // Pad arrays so table rows align
  const maxRows = Math.max(earnings.length + 1, deductions.length); // +1 for Basic Salary row
  const earningsRows: (SalaryComponent | null)[] = [null, ...earnings]; // null = Basic Salary row
  while (earningsRows.length < maxRows) earningsRows.push(null);
  const deductionsRows: (SalaryComponent | null)[] = [...deductions];
  while (deductionsRows.length < maxRows) deductionsRows.push(null);

  const generateQRCodeURL = () => {
    if (!employeePayroll || !payrollInfo) return '';
    const frontendURL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const monthNum = new Date(payrollInfo.start_date).getMonth() + 1;
    const yearNum = new Date(payrollInfo.start_date).getFullYear();
    const yearMonth = `${yearNum}${String(monthNum).padStart(2, '0')}`;
    const token = btoa(`${employeePayroll.id}-${yearMonth}`);
    return `${frontendURL}/salary-slip/${employeePayroll.id}/${token}`;
  };

  return (
    <Layout>
      <Container maxWidth="md">
        <Box sx={{ p: 3 }}>
          {/* Back Button */}
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(`/payroll/${payrollId}`)}
            sx={{ mb: 2, color: isDarkMode ? 'rgba(255,255,255,0.87)' : 'inherit' }}
          >
            Back to Payroll Details
          </Button>

          {/* Salary Slip Document */}
          <Box
            sx={{
              bgcolor: '#ffffff',
              color: '#111',
              p: { xs: 3, sm: 5 },
              fontFamily: '"Times New Roman", Times, serif',
              border: '1px solid #ddd',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            }}
          >
            {/* ── Company Header ── */}
            {/* Logo top-left with the details centred across the full width,
                mirroring the PDF header in utils/pdfGenerator.js. The symmetric px
                keeps that centring while stopping the text running under the logo
                on narrow screens — an absolutely positioned child is placed against
                the padding box, so the logo itself stays pinned to the edge. */}
            <Box sx={{ position: 'relative', textAlign: 'center', mb: 2, minHeight: 62, px: '70px' }}>
              <Box
                component="img"
                src={COMPANY_LOGO_SRC}
                alt={companyInfo?.company_name || 'Company logo'}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: 62,
                  height: 62,
                  objectFit: 'contain',
                }}
              />
              <Typography
                sx={{
                  fontFamily: '"Times New Roman", Times, serif',
                  fontWeight: 'bold',
                  fontSize: '1.35rem',
                  letterSpacing: '1.5px',
                  color: '#111',
                  textTransform: 'uppercase',
                }}
              >
                {companyInfo?.company_name || 'MKRAFT CAPABILITY SOLUTIONS LLP'}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#444', mt: 0.3 }}>
                {companyInfo?.company_address || 'Electronic City Phase 2, Bangalore, Karnataka'} -{' '}
                {companyInfo?.pincode || '560100'}
              </Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#444' }}>
                Phone: {companyInfo?.phone_number || '+91-XXXXXXXXXX'} | Email:{' '}
                {companyInfo?.email || 'hr@company.com'}
              </Typography>
            </Box>

            {/* ── Salary Slip Title ── */}
            <Typography
              sx={{
                fontFamily: '"Times New Roman", Times, serif',
                fontWeight: 'bold',
                fontSize: '1rem',
                color: '#111',
                mt: 3,
                mb: 2,
              }}
            >
              Salary Slip - [{month}, {year}]
            </Typography>

            {/* ── Employee Information ── */}
            <Typography
              sx={{
                fontFamily: '"Times New Roman", Times, serif',
                fontWeight: 'bold',
                fontStyle: 'italic',
                fontSize: '0.9rem',
                color: '#222',
                mb: 0.5,
              }}
            >
              Employee Information
            </Typography>
            <Table
              size="small"
              sx={{
                mb: 2,
                borderCollapse: 'collapse',
                '& td': { border: '1px solid #bbb', py: 0.8, px: 1.5, fontSize: '0.82rem' },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell sx={{ ...labelCell }}>Employee Name</TableCell>
                  <TableCell sx={{ ...valueCell }}>{employeePayroll.employeeName}</TableCell>
                  <TableCell sx={{ ...labelCell }}>PAN Number</TableCell>
                  <TableCell sx={{ ...valueCell }}>{employeePayroll.panNumber || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...labelCell }}>Bank Name</TableCell>
                  <TableCell sx={{ ...valueCell }}>{employeePayroll.bankName || '—'}</TableCell>
                  <TableCell sx={{ ...labelCell }}>Account Number</TableCell>
                  <TableCell sx={{ ...valueCell }}>{employeePayroll.accountNumber || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...labelCell }}>UAN Number</TableCell>
                  <TableCell sx={{ ...valueCell, width: '80%' }} colSpan={3}>
                    {employeePayroll.uanNumber || '—'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ── Pay Period Details ── */}
            <Typography
              sx={{
                fontFamily: '"Times New Roman", Times, serif',
                fontWeight: 'bold',
                fontStyle: 'italic',
                fontSize: '0.9rem',
                color: '#222',
                mb: 0.5,
              }}
            >
              Pay Period Details
            </Typography>
            <Table
              size="small"
              sx={{
                mb: 2,
                borderCollapse: 'collapse',
                '& td': { border: '1px solid #bbb', py: 0.8, px: 1.5, fontSize: '0.82rem' },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell sx={{ ...labelCell, width: '20%' }}>Pay Period</TableCell>
                  <TableCell sx={{ ...valueCell, width: '80%' }} colSpan={3}>
                    {formatDate(payrollInfo.start_date)} to {formatDate(payrollInfo.end_date)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ ...labelCell }}>Pay Date</TableCell>
                  <TableCell sx={{ ...valueCell }} colSpan={3}>
                    {formatDate(payrollInfo.pay_date)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ── Compensation Breakdown ── */}
            <Typography
              sx={{
                fontFamily: '"Times New Roman", Times, serif',
                fontWeight: 'bold',
                fontStyle: 'italic',
                fontSize: '0.9rem',
                color: '#222',
                mb: 0.5,
              }}
            >
              Compensation Breakdown
            </Typography>
            <Table
              size="small"
              sx={{
                mb: 3,
                borderCollapse: 'collapse',
                '& td, & th': { border: '1px solid #bbb', py: 0.8, px: 1.5, fontSize: '0.82rem' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', width: '30%' }}>Earnings</TableCell>
                  <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', width: '20%' }}>Amount (Rs.)</TableCell>
                  <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', width: '30%' }}>Deductions</TableCell>
                  <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', width: '20%' }}>Amount (Rs.)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Basic Salary always first */}
                <TableRow>
                  <TableCell>Basic Salary</TableCell>
                  <TableCell>{employeePayroll.grossSalary.toLocaleString()}</TableCell>
                  <TableCell>{deductionsRows[0]?.label || ''}</TableCell>
                  <TableCell>
                    {deductionsRows[0] ? getComponentAmount(deductionsRows[0]).toLocaleString() : ''}
                  </TableCell>
                </TableRow>

                {/* Remaining rows */}
                {Array.from({ length: maxRows - 1 }).map((_, i) => {
                  const earning = earningsRows[i + 1]; // skip index 0 (Basic)
                  const deduction = deductionsRows[i + 1];
                  return (
                    <TableRow key={i}>
                      <TableCell>{earning?.label || ''}</TableCell>
                      <TableCell>{earning ? getComponentAmount(earning).toLocaleString() : ''}</TableCell>
                      <TableCell>{deduction?.label || ''}</TableCell>
                      <TableCell>{deduction ? getComponentAmount(deduction).toLocaleString() : ''}</TableCell>
                    </TableRow>
                  );
                })}

                {/* Totals row */}
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Earnings</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{totalEarnings.toLocaleString()}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Deductions</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{totalDeductions.toLocaleString()}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ── Net Salary ── */}
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  fontFamily: '"Times New Roman", Times, serif',
                  fontWeight: 'bold',
                  fontSize: '1.05rem',
                  color: '#111',
                }}
              >
                Net Salary: Rs. {employeePayroll.netSalary.toLocaleString()}
              </Typography>
            </Box>

            {/* ── Signature & Footer ── */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 2 }}>
              {/* Left: Signature + footer note */}
              <Box>
                <Typography sx={{ fontSize: '0.82rem', color: '#444', mb: 4 }}>Signature</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
                  For any queries, please contact the HR department.
                </Typography>
                
              </Box>

              {/* Right: QR Code */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    p: '6px',
                    background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                    borderRadius: '10px',
                    boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                  }}
                >
                  <Box sx={{ p: '5px', bgcolor: '#fff', borderRadius: '6px' }}>
                    <QRCodeSVG
                      value={generateQRCodeURL()}
                      size={80}
                      level="H"
                      includeMargin={false}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      imageSettings={{
                        src: '/assets/logos/transparent_m.PNG',
                        x: undefined,
                        y: undefined,
                        height: 24,
                        width: 24,
                        excavate: true,
                      }}
                    />
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.5px', color: '#333' }}>
                  SCAN TO VIEW
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: '#888' }}>Salary Slip Online</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </Layout>
  );
};

export default ViewEmployeeSalarySlip;